import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { TagSearchInput } from '@features/query/form/TagInput/index';
import { FilterableDocumentsContext } from '@/contexts/FilterableDocumentsContext';
import { convertHexToRGBA, truncate } from '@utils/usefulFuncs';
import { fetchDocumentsForTags, fetchNewsDocumentsForTags } from '@utils/doc';

const TagSelectBarWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const InputStyling = styled.div`
  input {
    width: 100%;
    height: 42px;
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    border-radius: 6px;
    padding: 8px;
    cursor: text;
  }
`;
const CompanyTagList = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 8px;
`;
const TickerWrapper = styled.div`
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.text};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.14s;
  min-width: 66px;
  text-align: center;

  &:hover {
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.1)};
    border: 1px solid ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 1)};

    * {
      color: ${({ theme }) => theme.themeColors.primaryCtaButton};
    }
  }

  &.selected {
    border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};

    * {
      color: ${({ theme }) => theme.themeColors.buttonText};
    }
  }
`;

function isTagActive(tag, activeTag) {
  if (activeTag?.doc_tag_id) {
    return tag.doc_tag_id === activeTag.doc_tag_id;
  }
  if (activeTag?.company_id) {
    return tag.company_id === activeTag.company_id;
  }
  return false;
}

const CompanyTag = ({ tag, isActive, onClick }) => {
  const handleClick = () => {
    onClick(tag, isActive);
  };

  const tagName = truncate(tag.name, 24);
  if (isActive) {
    return (
      <TickerWrapper onClick={handleClick} className="selected">
        <Body5 bold>{tagName}</Body5>
      </TickerWrapper>
    );
  }
  return (
    <TickerWrapper onClick={handleClick}>
      <Body5 bold>{tagName}</Body5>
    </TickerWrapper>
  );
};

export const TagSelectBar = ({ tags, onAddTag }) => {
  const { addDocuments, applyFilter, clearFilter, setLoadingDocuments } = React.useContext(FilterableDocumentsContext);
  const [activeTag, setActiveTag] = React.useState(null);

  React.useEffect(() => {
    if (tags.length > 0) {
      const defaultActiveTag = tags[0];
      setActiveTag(defaultActiveTag);
      applyFilter({ tags: [defaultActiveTag] });
    }
  }, [tags]);

  const handleAddTag = async (tag) => {
    onAddTag(tag);
    setActiveTag(tag);
    setLoadingDocuments(true);
    const [companyDocs, newsDocs] = await Promise.all([fetchDocumentsForTags([tag]), fetchNewsDocumentsForTags([tag])]);
    setLoadingDocuments(false);
    addDocuments([...(companyDocs || []), ...(newsDocs || [])], { tags: [tag] });
  };

  const onTagClick = (tag, isActive) => {
    setActiveTag(tag);
    applyFilter({ tags: [tag] });

    if (isActive) {
      setActiveTag(null);
      clearFilter('tags');
    }
  };

  return (
    <TagSelectBarWrapper>
      <InputStyling>
        <TagSearchInput placeholder="Search for companies or tags" onAddTag={handleAddTag} showCompanyTags />
      </InputStyling>
      {tags.length > 0 && (
        <>
          <Container top={16}>
            <Body5>Tags</Body5>
          </Container>
          <CompanyTagList>
            {tags.map((tag) => (
              <CompanyTag key={tag.id} tag={tag} isActive={isTagActive(tag, activeTag)} onClick={onTagClick} />
            ))}
          </CompanyTagList>
        </>
      )}
    </TagSelectBarWrapper>
  );
};
